export const VAT_RATE = 0.1;

const REAL_VAT_EFFECTIVE_AT_UTC = new Date('2026-06-18T00:00:00Z');

const resolveVatEffectiveAtUtc = () => {
  const raw = process.env.NEXT_PUBLIC_VAT_EFFECTIVE_AT_OVERRIDE;
  const parsed = raw ? new Date(raw) : REAL_VAT_EFFECTIVE_AT_UTC;

  if (!Number.isNaN(parsed.getTime())) {
    return parsed;
  }

  const error = new Error('Invalid VAT_EFFECTIVE_AT override');
  if (process.env.NEXT_PUBLIC_STAGE !== 'PRD') {
    throw error;
  }

  console.error(error);
  return REAL_VAT_EFFECTIVE_AT_UTC;
};

/**
 * VAT 정책 경계 시각 (서버 VatPolicyConstants.EffectiveAtUtc와 1:1).
 * 2026-06-18T09:00:00+09:00 == 2026-06-18T00:00:00Z
 */
export const VAT_EFFECTIVE_AT_UTC = resolveVatEffectiveAtUtc();

/**
 * 클라이언트 VAT preview가 허용되는 시나리오.
 * 서버 응답(finalPrice/vatAmount) 없이 버튼/PG 금액을 먼저 그려야 하는
 * "신규 결제" 경로에서만 사용한다.
 * 정기 갱신/업그레이드/기존 결제 이력 표시에는 사용 금지 —
 * 해당 경로는 서버 snapshot(expected price, persisted amount)이 source of truth다.
 */
export type VatPreviewScenario =
  | 'NEW_SUBSCRIPTION_FIRST_PAYMENT'
  | 'PRODUCT_ONE_TIME_PAYMENT'
  | 'CUSTOM_ONE_TIME_PAYMENT';

export type GetVatPreviewParams = {
  /** VAT 적용 전 실제 결제 대상 금액 (할인/차감 반영 후) */
  amount: number;
  currency: string;
  userCountryCode: string | null | undefined;
  scenario: VatPreviewScenario;
  /** 테스트 주입용. 미지정 시 현재 시각 */
  now?: Date;
};

export type VatPreviewResult = {
  isApplicable: boolean;
  vatAmount: number;
  finalPrice: number;
};

/**
 * 신규 결제 preview 전용 VAT 계산.
 * 최종 과금 판단의 source of truth는 서버 snapshot이며,
 * 이 결과는 서버 금액을 받기 전의 보조 표시(optimistic display)로만 쓴다.
 * 계산식은 서버와 동일: vatAmount = round(amount * 10%)
 */
export const getVatPreview = (
  params: GetVatPreviewParams,
): VatPreviewResult => {
  const { amount, currency, userCountryCode, now = new Date() } = params;
  const normalizedUserCountryCode = userCountryCode?.trim().toUpperCase();
  const normalizedCurrency = currency?.trim().toUpperCase();

  const isApplicable =
    normalizedUserCountryCode === 'KR' &&
    normalizedCurrency === 'KRW' &&
    now.getTime() >= VAT_EFFECTIVE_AT_UTC.getTime() &&
    amount > 0;

  if (!isApplicable) {
    return { isApplicable: false, vatAmount: 0, finalPrice: amount };
  }

  const vatAmount = Math.round(amount * VAT_RATE);

  return { isApplicable: true, vatAmount, finalPrice: amount + vatAmount };
};
